/**
 * The e-mail dialog of the account (settings: the account dialog's "E-mail", where the server offers e-mail sign-in;
 * src/server/core/services/email.ts). A modal popup in the frame of the game's popups, like the delete-account dialog:
 *  - the account's sign-in address (data, never translated) or that it has none, and an address waiting for its
 *    confirmation link (auth.emailStatus);
 *  - a new address and the account's password: "Send the link" (auth.emailChange; the new address works once its link
 *    is opened, the current one meanwhile), "once more" for the pending address's link (auth.emailResend), back.
 * Behaviour as in the delete-account dialog: Enter moves from the address to the password and sends from there,
 * Escape and the Android back button close it; one request at a time (the button says "Please wait…"); a wrong
 * password keeps the session (UNAUTHORIZED 'invalid_credentials' of the change is no ended session); the password
 * lives only in its field and in the one request; a phone zooms into it (focusRect) or shows it larger (wide view).
 */
import { Event, KeyboardEvent, Point, Sprite, TextField } from '@flash';
import { PASSWORD_MAX } from '../../shared/auth-rules';
import { EMAIL_MAX, type EmailProblem, checkEmail } from '../../shared/email-rules';
import * as T from '../runtime/auth-texts';
import { language, translate } from '../runtime/i18n';
import { netErrorCode } from '../runtime/net-policy';
import { EMAIL_RESTRICT, failureText } from './auth-screen';
import { BigButton, ERROR_COLOR, HINT_COLOR, LINK_COLOR, SmallButton, TEXT_COLOR, inputField, label, popupFrame, setRawText } from './controls';
import { fitDialogBody, hideScreenUnder } from './port-dialog';

export interface EmailDialogOptions {
  /** The session ended meanwhile (UNAUTHORIZED other than a wrong password): the game handles it like any ended session. */
  onSessionEnded(): void;
  onClosed(): void;
}

const PANEL_SCALE = 0.8;
const FRAME_W = 344;
const FRAME_H = 389;
const FRAME_X = Math.round((807 - FRAME_W) / 2);
const FRAME_Y = Math.round((600 - FRAME_H) / 2);
const COL_X = 26;
const COL_W = 270;
const FOCUS_MARGIN = 14;
const FOCUS_BOTTOM = 348;
const DIM = { x: -2000, y: -1000, w: 4807, h: 2600, alpha: 0.5 };

export class EmailDialog {
  readonly root = new Sprite();
  busy = false;
  /** auth.emailChange / auth.emailResend requests sent (tests). */
  requests = 0;
  readonly current: TextField;
  readonly pending: TextField;
  readonly email: TextField;
  readonly emailMessage: TextField;
  readonly password: TextField;
  readonly status: TextField;
  readonly sendButton: BigButton;
  readonly resendButton: SmallButton;
  readonly backButton: SmallButton;
  private pendingEmail: string | null = null;
  private disposed = false;
  private stageRef: any = null;
  private readonly body = new Sprite();
  private readonly restoreUnder: () => void;

  constructor(
    private readonly fwp: any,
    private readonly opts: EmailDialogOptions,
  ) {
    this.root.name = '$email_dialog';
    const dim = new Sprite();
    dim.name = '$email_dialog_dim';
    dim.graphics.beginFill(0x000000, DIM.alpha);
    dim.graphics.drawRect(DIM.x, DIM.y, DIM.w, DIM.h);
    dim.graphics.endFill();
    this.root.addChild(dim);
    this.body.name = '$email_dialog_body';
    this.root.addChild(this.body);
    const panel = popupFrame(fwp, FRAME_X, FRAME_Y, PANEL_SCALE);
    panel.name = '$email_dialog_panel';
    this.body.addChild(panel);
    const page = new Sprite();
    page.name = '$email_dialog_content';
    page.x = FRAME_X;
    page.y = FRAME_Y;
    this.body.addChild(page);
    this.restoreUnder = hideScreenUnder(fwp);

    place(page, named(label(T.MSG_EMAIL_TITLE, { size: 14, bold: true, align: 'center', width: COL_W }), 'email_title'), COL_X, 20);
    this.current = named(label(T.MSG_EMAIL_LOADING, { size: 12, color: TEXT_COLOR, width: COL_W, lines: 3 }), 'email_current');
    this.current.height = 52;
    place(page, this.current, COL_X, 44);
    this.pending = named(label('', { size: 11, color: HINT_COLOR, width: COL_W, lines: 2 }), 'email_pending');
    this.pending.height = 32;
    place(page, this.pending, COL_X, 92);
    place(page, named(label(T.MSG_EMAIL_NEW, { bold: true }), 'email_new_label'), COL_X, 124);
    this.email = inputField('email_new', { width: COL_W, maxChars: EMAIL_MAX, restrict: EMAIL_RESTRICT });
    this.email.$enterKeyHint = 'next';
    this.email.$inputMode = 'email';
    place(page, this.email, COL_X, 142);
    this.emailMessage = named(label('', { size: 11, color: ERROR_COLOR, width: COL_W, lines: 1 }), 'email_new_message');
    place(page, this.emailMessage, COL_X, 165);
    place(page, named(label(T.MSG_EMAIL_PASSWORD, { bold: true }), 'email_password_label'), COL_X, 182);
    this.password = inputField('email_password', { width: COL_W, maxChars: PASSWORD_MAX, password: true });
    place(page, this.password, COL_X, 200);
    this.status = named(label('', { size: 12, bold: true, color: ERROR_COLOR, align: 'center', width: COL_W, lines: 2 }), 'email_status');
    this.status.height = 36;
    place(page, this.status, COL_X, 226);
    this.sendButton = new BigButton(fwp, 'email_send', T.MSG_EMAIL_SEND);
    this.sendButton.addEventListener('click', () => this.submit());
    place(page, this.sendButton, COL_X + (COL_W - this.sendButton.width) / 2, 264);
    this.resendButton = new SmallButton(fwp, 'email_resend', T.MSG_EMAIL_RESEND);
    this.resendButton.addEventListener('click', () => this.resend());
    this.resendButton.visible = false;
    this.backButton = new SmallButton(fwp, 'email_back', T.MSG_DELETE_CANCEL);
    this.backButton.addEventListener('click', () => this.cancel());
    this.layoutRow();
    for (const f of [this.email, this.password]) {
      f.addEventListener(Event.CHANGE, () => {
        if (this.busy) return;
        this.status.text = '';
        this.emailMessage.text = '';
      });
    }
    this.root.addEventListener(Event.ENTER_FRAME, this.onFrame);
    this.load();
  }

  /** The last row: "once more" (a pending address) and back, each a finger's target of its half of the row. */
  private layoutRow(): void {
    const page = this.sendButton.parent as Sprite;
    const top = 310;
    const shown = this.resendButton.visible ? [this.resendButton, this.backButton] : [this.backButton];
    const gap = 16;
    const total = shown.reduce((a, b) => a + b.width, 0) + gap * (shown.length - 1);
    let x = COL_X + (COL_W - total) / 2;
    shown.forEach((b, i) => {
      place(page, b, x, top + 6);
      const left = i === 0 ? COL_X - FOCUS_MARGIN : x - gap / 2;
      const right = i === shown.length - 1 ? COL_X + COL_W + FOCUS_MARGIN : x + b.width + gap / 2;
      b.setHitRect({ x: left - b.x, y: top - b.y, width: right - left, height: FOCUS_BOTTOM - top });
      x += b.width + gap;
    });
  }

  /** auth.emailStatus: the current and the pending address. */
  private load(): void {
    Promise.resolve(this.fwp.services.emailStatus()).then(
      (s: { email: string | null; pendingEmail: string | null }) => this.showStatus(s),
      (error: any) => {
        if (this.disposed) return;
        if (netErrorCode(error) === 'UNAUTHORIZED') return this.endSession();
        this.current.text = T.MSG_LINK_FAILED;
      },
    );
  }

  private showStatus(s: { email: string | null; pendingEmail: string | null }): void {
    if (this.disposed) return;
    // The addresses are data: never translated.
    if (s.email) setRawText(this.current, `${translate(T.MSG_EMAIL_CURRENT)} ${s.email}`);
    else this.current.text = T.MSG_EMAIL_NONE;
    this.pendingEmail = s.pendingEmail;
    if (s.pendingEmail) setRawText(this.pending, `${translate(T.MSG_EMAIL_PENDING)} ${s.pendingEmail}`);
    else this.pending.text = '';
    this.resendButton.visible = !!s.pendingEmail;
    this.layoutRow();
  }

  attach(): void {
    const st = this.root.stage;
    if (!st || this.stageRef) return;
    this.stageRef = st;
    st.addEventListener(KeyboardEvent.KEY_DOWN, this.onKey);
    st.focus = this.email;
  }

  focusRect(): { x: number; y: number; width: number; height: number } | null {
    if (this.disposed || !this.root.stage) return null;
    if ((this.root.stage as any).$visibleArea) return null;
    const a = this.root.localToGlobal(new Point(FRAME_X + COL_X - FOCUS_MARGIN, FRAME_Y + 4));
    const b = this.root.localToGlobal(new Point(FRAME_X + COL_X + COL_W + FOCUS_MARGIN, FRAME_Y + FOCUS_BOTTOM));
    return { x: Math.round(a.x), y: Math.round(a.y), width: Math.round(b.x - a.x), height: Math.round(b.y - a.y) };
  }

  back(): boolean {
    if (!this.disposed && !this.busy) this.cancel();
    return true;
  }

  cancel(): void {
    if (this.busy || this.disposed) return;
    this.dispose();
    this.opts.onClosed();
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    if (this.stageRef) {
      this.stageRef.removeEventListener(KeyboardEvent.KEY_DOWN, this.onKey);
      if (this.stageRef.focus === this.password || this.stageRef.focus === this.email) this.stageRef.focus = null;
    }
    this.stageRef = null;
    this.root.removeEventListener(Event.ENTER_FRAME, this.onFrame);
    this.password.text = '';
    if (this.root.parent) this.root.parent.removeChild(this.root);
    this.restoreUnder();
  }

  private readonly onKey = (e: any): void => {
    if (this.disposed) return;
    const st = this.root.stage;
    if (e.keyCode === 13 && st && st.focus === this.email) {
      if (!this.busy) st.focus = this.password;
    } else if (e.keyCode === 13) this.submit();
    else if (e.keyCode === 27) this.cancel();
  };

  /** "Send the link": the address checked by the shared rules first; the password leaves its field for the request. */
  submit(): void {
    if (this.busy || this.disposed) return;
    const st = this.root.stage;
    const c = checkEmail(this.email.text);
    if (!c.ok) {
      this.emailMessage.text = T.EMAIL_PROBLEM_TEXT[c.reason];
      if (st) st.focus = this.email;
      return;
    }
    if (this.password.text === '') {
      this.showError(T.CREDENTIAL_PROBLEM_TEXT.password_required);
      return;
    }
    const password = this.password.text;
    this.password.text = '';
    this.run(() => this.fwp.services.changeEmail(c.email, password, language()));
  }

  /** "Once more": the pending address's link again. */
  resend(): void {
    if (this.busy || this.disposed || !this.pendingEmail) return;
    this.run(() => this.fwp.services.resendEmail(language()));
  }

  private run(request: () => Promise<{ email: string | null; pendingEmail: string | null }>): void {
    this.status.text = '';
    this.emailMessage.text = '';
    this.setBusy(true);
    this.requests++;
    Promise.resolve(request()).then(
      (s) => {
        if (this.disposed) return;
        this.setBusy(false);
        this.showStatus(s);
        this.email.text = '';
        this.status.textColor = LINK_COLOR;
        this.status.text = T.MSG_EMAIL_SENT;
      },
      (error: any) => this.failed(error),
    );
  }

  showError(text: string): void {
    this.status.textColor = ERROR_COLOR;
    this.status.text = text;
    const st = this.root.stage;
    if (st) st.focus = this.password;
  }

  private endSession(): void {
    this.dispose();
    this.opts.onSessionEnded();
  }

  private failed(error: any): void {
    if (this.disposed) return;
    this.setBusy(false);
    const code = netErrorCode(error);
    const details = error && error.details ? error.details : {};
    const reason = typeof details.reason === 'string' ? details.reason : '';
    if (code === 'UNAUTHORIZED' && reason === 'invalid_credentials') return this.showError(T.MSG_DELETE_WRONG_PASSWORD);
    if (code === 'UNAUTHORIZED') return this.endSession();
    if (code === 'VALIDATION_FAILED' && reason in T.EMAIL_PROBLEM_TEXT) {
      this.emailMessage.text = T.EMAIL_PROBLEM_TEXT[reason as EmailProblem];
      const st = this.root.stage;
      if (st) st.focus = this.email;
      return;
    }
    if (code === 'VALIDATION_FAILED' && reason === 'password_required') return this.showError(T.CREDENTIAL_PROBLEM_TEXT.password_required);
    if (code === 'RATE_LIMITED' && reason === 'too_many_attempts') return this.showError(T.MSG_DELETE_TOO_MANY);
    if (code === 'RATE_LIMITED' && reason === 'email_send_limit') return this.showError(T.MSG_AUTH_EMAIL_LIMIT);
    this.showError(failureText(code, reason));
  }

  private setBusy(on: boolean): void {
    this.busy = on;
    this.sendButton.enabled = !on;
    this.sendButton.setText(on ? T.MSG_AUTH_WAIT : T.MSG_EMAIL_SEND);
    this.resendButton.enabled = !on;
    this.backButton.enabled = !on;
    for (const f of [this.email, this.password]) {
      f.type = on ? 'dynamic' : 'input';
      f.selectable = !on;
    }
    const st = this.root.stage;
    if (on && st && (st.focus === this.password || st.focus === this.email)) st.focus = null;
  }

  private readonly onFrame = (): void => {
    const st = this.root.stage;
    fitDialogBody(this.body, { x: FRAME_X, y: FRAME_Y, width: FRAME_W, height: FRAME_H }, st);
    for (const f of [this.email, this.password]) {
      const color = st && st.focus === f ? LINK_COLOR : 0x000000;
      if (f.borderColor !== color) f.borderColor = color;
    }
  };
}

function place(parent: Sprite, child: any, x: number, y: number): void {
  child.x = Math.round(x);
  child.y = Math.round(y);
  parent.addChild(child);
}

function named<O extends { name: string }>(o: O, name: string): O {
  o.name = name;
  return o;
}
